import type { BoardSummary, GapBucket, GapSideSummary, OpenedToday, Scoreboard as Score } from '@claims/core';
import { countOf } from './count.js';
import { Shape } from './icons.js';

// The scoreboard band on the Matters page (Rod 2026-10-07: "there isnt overall numbers to look at"; "i like our goal of the daily but we
// should know how many cases dont have an def or plt ins and what we need to open them").

export type Pick = { side: 'DEF' | 'PLT'; key: string } | null;
const WHEN: [string, string][] = [['today', 'Came in today'], ['yesterday', 'Yesterday'], ['week', 'Last 7 days']];
const WHO_LABEL = { person: 'a person', system: 'the system', nothing: 'nothing to open' } as const;
const pct = (n: number, d: number) => (d ? Math.round((100 * n) / d) : 0);

export function Goal({ t }: { t: OpenedToday | null | undefined }) {
  if (!t) return <div className="sc-goal"><div className="sc-h">Claim numbers added today, all cases</div><div className="sc-na">Not available right now (Salesforce did not answer).</div></div>;
  const team = t.noAi - t.bySystem;
  const scale = Math.max(t.goal, t.total) || 1;
  const w = (n: number) => `${(100 * n) / scale}%`;
  const why = 'Same rule as the 5:30 pm Claims Team report: a claim number first entered today on a PLT or DEF record, as of now. It counts numbers added, not proof the claim is new: the carrier may already have had it. Second clients from the same crash are not counted: they carry the main client\'s claim.';
  return (
    <div className="sc-goal" title={why}>
      <div className="sc-h">Claim numbers added today, all cases</div>
      <div className="sc-n"><b>{countOf(t.total)}</b><span> of {t.goal} a day</span><span className={`sc-pct${t.total >= t.goal ? ' ok' : ''}`}>{pct(t.total, t.goal)}%</span>
        <span className="sc-pd">PLT {t.plt} · DEF {t.def}</span></div>
      <div className="sc-bar" role="img" aria-label={`${t.total} of ${t.goal}`}>
        <i className="s-ai" style={{ width: w(t.ai) }} /><i className="s-hand" style={{ width: w(t.handoff ?? 0) }} /><i className="s-after" style={{ width: w(t.afterAi) }} /><i className="s-team" style={{ width: w(team) }} />
        <i className="s-sys" style={{ width: w(t.bySystem) }} /><span className="sc-mark" style={{ left: w(t.goal) }} title={`goal ${t.goal}`} />
      </div>
      {/* change 6 (Kimi's UI assessment, Rod 2026-10-13 "1 a"): a split that is 0 today is not shown ("0 moved from the policy field") */}
      <div className="sc-split">
        {t.ai ? <span><i className="swc ai" />{t.ai} from an AI call</span> : null}
        {t.handoff ? <span title="The AI reached the carrier's rep and handed the call to our staff, and the number came in within 24 hours"><i className="swc hand" />{t.handoff} the AI reached the rep, staff finished</span> : null}
        {t.afterAi ? <span><i className="swc after" />{t.afterAi} entered by our team after an AI call didn't get one</span> : null}
        {team ? <span><i className="swc team" />{team} entered by our team</span> : null}
        {t.bySystem ? <span title="Entered by the automation with no call first: a claim number moved out of the policy field (second clients' copies of the main claim are not counted)"><i className="swc sys" />{t.bySystem} moved from the policy field</span> : null}
        {t.intake ? <span className="mt">+{t.intake} already open at intake</span> : null}
        {t.companions ? <span className="mt" title="A second client from the same crash carries the main client's claim, so it is not a new claim">{t.companions} second {t.companions === 1 ? 'client' : 'clients'} not counted</span> : null}
      </div>
    </div>
  );
}

function Bucket({ b, picked, onPick }: { b: GapBucket; picked: boolean; onPick: (p: Pick) => void }) {
  const tip = `${b.count} case${b.count > 1 ? 's' : ''}: ${b.label}. Need: ${b.need}. Next move: ${WHO_LABEL[b.who]}. Click to show only these.`;
  return (
    <button type="button" className={`gb who-${b.who}`} aria-pressed={picked} title={tip} onClick={() => onPick(picked ? null : { side: b.side, key: b.key })}>
      <b>{countOf(b.count)}</b><span className="gbl">{b.label}</span>{b.who === 'person' ? <span className="gbn">Need: {b.need}</span> : null}
    </button>
  );
}

function GapCol({ side, s, cases, pick, onPick }: { side: 'DEF' | 'PLT'; s: GapSideSummary; cases: number; pick: Pick; onPick: (p: Pick) => void }) {
  // every reason shows (never folded away), need-a-person first, then the system, then nothing to open (gapSummary's order)
  const sum = (who: GapBucket['who']) => s.buckets.filter((b) => b.who === who).reduce((n, b) => n + b.count, 0);
  return (
    <div className="gcol">
      <div className="gh" title={s.placeholder ? `${s.placeholder} of them have a ${side} record whose carrier is a placeholder (Pending, Unknown)` : undefined}>
        <b>{countOf(s.missing)}</b> of {countOf(cases)} cases have no {side} to open</div>
      <div className="gsub">{[
        sum('person') ? <span key="p" className="who-person"><b>{countOf(sum('person'))}</b> need a person</span> : null,
        sum('system') ? <span key="s" className="who-system"><b>{countOf(sum('system'))}</b> the system is working</span> : null,
        sum('nothing') ? <span key="n" className="who-nothing"><b>{countOf(sum('nothing'))}</b> nothing to open</span> : null,
      ].filter(Boolean).flatMap((x, i) => (i ? [' · ', x] : [x]))}</div>
      {s.missing ? <div className={`gbs${s.buckets.length > 6 ? ' dense' : ''}`}>{s.buckets.map((b) => <Bucket key={b.key} b={b} picked={!!pick && pick.side === side && pick.key === b.key} onPick={onPick} />)}</div>
        : <div className="mt gnone">Every case has a {side} insurance record with a carrier.</div>}
    </div>
  );
}

export function Scoreboard({ score, summary, when, setWhen, label, onlyNeed, setOnlyNeed, pick, onPick, right, onlyReplies, setOnlyReplies, showNeed = true }: {
  score: Score | undefined; summary: BoardSummary; when: string; setWhen: (w: string) => void; label: string;
  onlyNeed: boolean; setOnlyNeed: (v: boolean) => void; pick: Pick; onPick: (p: Pick) => void; right?: import('react').ReactNode;
  onlyReplies?: boolean; setOnlyReplies?: (v: boolean) => void;
  /** the records-only "need you" filter: All cases view only (change 8: the To do view's count is the page's one "need you" number) */
  showNeed?: boolean;
}) {
  return (
    <section className="score" aria-label="Numbers">
      <Goal t={score?.today} />
      <div className="sc-win">
        <div className="sc-top">
          <div className="seg" role="group" aria-label="Which cases">
            {WHEN.map(([k, l]) => <button key={k} type="button" aria-pressed={when === k} onClick={() => { onPick(null); setWhen(k); }}>{l}</button>)}
          </div>
          <span className="sc-tot" title={`${summary.converted} ${label}; ${summary.withInsurance} with an insurance record; ${summary.opened} with a claim number of ${summary.possible} possible (the 5:30 pm report's numbers; second clients from the same crash are not counted)`}>
            <b>{countOf(summary.converted)}</b> converted · <b>{countOf(summary.withInsurance)}</b> insured · <b>{countOf(summary.opened)}</b> of <b>{countOf(summary.possible)}</b> records have a claim #
            {' '}<span className="wk"><Shape w="provider" /><b>{countOf(summary.working)}</b> working</span></span>
          {showNeed ? <button type="button" className="needbtn sm" aria-pressed={onlyNeed} onClick={() => setOnlyNeed(!onlyNeed)} title="Show only the insurance records a person must act on (missing sides are counted below; the To do view lists both)">
            <Shape w="team" />Only records that need you <b>{countOf(summary.need)}</b>{onlyNeed ? ' (on)' : ''}</button> : null}
          {score?.replies && setOnlyReplies ? <RepliesChip r={score.replies} on={!!onlyReplies} set={setOnlyReplies} /> : null}
          <span className="grow" />
          {right}
        </div>
        {score ? (
          <div className="sc-gaps">
            <GapCol side="DEF" s={score.gaps.def} cases={score.gaps.cases} pick={pick} onPick={onPick} />
            <GapCol side="PLT" s={score.gaps.plt} cases={score.gaps.cases} pick={pick} onPick={onPick} />
          </div>
        ) : null}
      </div>
    </section>
  );
}

/** Client text replies waiting for a person (last 14 days of cases): a filter, with today's count in the hover. */
export function RepliesChip({ r, on, set }: { r: { waiting: number; today: number }; on: boolean; set: (v: boolean) => void }) {
  return (
    <button type="button" className={`needbtn sm repbtn${r.waiting ? ' hot' : ''}`} aria-pressed={on} onClick={() => set(!on)}
      title={`${r.waiting} client text repl${r.waiting === 1 ? 'y' : 'ies'} waiting for a person (last 14 days of cases); ${r.today} replies came in today`}>
      <b>{countOf(r.waiting)}</b> client repl{r.waiting === 1 ? 'y' : 'ies'} waiting{on ? ' (only these)' : ''}</button>
  );
}
